'use client';

import * as React from 'react';
import toast from 'react-hot-toast';
import { Loader2 } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Funnel, useBulkDeleteFunnels, useDemoteFunnelToStaging } from '@/lib/api/funnels';

type Phase = 'idle' | 'demoting' | 'deleting';

const isLive = (f: Funnel) => (f.productionDomains?.length ?? 0) > 0;

/**
 * Delete one or more funnels behind a confirm dialog (F07/CRM-063).
 *
 * The server hard-blocks deleting a funnel that is promoted to a custom domain,
 * so any live target is demoted to staging FIRST (with a loading state), then
 * all targets are deleted. Used by both the per-row menu and the bulk action bar.
 */
export function useDeleteFunnelsDialog(storeId: string, onDone?: () => void) {
  const [targets, setTargets] = React.useState<Funnel[] | null>(null);
  const [phase, setPhase] = React.useState<Phase>('idle');
  const demoteMutation = useDemoteFunnelToStaging();
  const bulkDeleteMutation = useBulkDeleteFunnels();

  const busy = phase !== 'idle';
  const liveTargets = (targets ?? []).filter(isLive);

  const open = React.useCallback((funnels: Funnel[]) => {
    if (funnels.length > 0) setTargets(funnels);
  }, []);

  const close = React.useCallback(() => {
    if (!busy) setTargets(null);
  }, [busy]);

  const confirm = React.useCallback(async () => {
    if (!targets || !storeId) return;
    try {
      // 1. Take any live funnel offline first — delete is blocked while promoted.
      if (liveTargets.length > 0) {
        setPhase('demoting');
        for (const f of liveTargets) {
          await demoteMutation.mutateAsync({
            funnelId: f.id,
            storeId,
            customDomain: f.productionDomains![0],
          });
        }
      }

      // 2. Delete all targets (bulkDelete handles one or many).
      setPhase('deleting');
      const result = await bulkDeleteMutation.mutateAsync({
        funnelIds: targets.map((f) => f.id),
        storeId,
      });

      if (result.deleted > 0) {
        toast.success(result.deleted === 1 ? 'Funnel deleted' : `${result.deleted} funnels deleted`);
      }
      if (result.failed > 0 && result.errors?.length) {
        result.errors.forEach((e: string) => toast.error(e, { duration: 6000 }));
      }

      setTargets(null);
      onDone?.();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to delete funnel');
    } finally {
      setPhase('idle');
    }
  }, [targets, storeId, liveTargets, demoteMutation, bulkDeleteMutation, onDone]);

  const count = targets?.length ?? 0;

  const dialog = (
    <AlertDialog open={!!targets} onOpenChange={(o) => { if (!o) close(); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {count === 1 ? 'Delete this funnel?' : `Delete ${count} funnels?`}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {liveTargets.length > 0 ? (
              <>
                {liveTargets.length === 1
                  ? `"${liveTargets[0].name}" is live on ${liveTargets[0].productionDomains![0]}. `
                  : `${liveTargets.length} of the selected funnels are live. `}
                They will be taken offline (demoted to staging) first, then deleted. This cannot be undone.
              </>
            ) : (
              <>
                This permanently deletes {count === 1 ? 'the funnel' : 'the selected funnels'} and removes
                the associated routes. This cannot be undone.
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
          <Button variant="destructive" onClick={confirm} disabled={busy}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {phase === 'demoting'
              ? 'Taking offline…'
              : phase === 'deleting'
                ? 'Deleting…'
                : liveTargets.length > 0
                  ? 'Demote & delete'
                  : 'Delete'}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return { open, dialog };
}
